import React from 'react';
import useInView from '../hooks/useInView';

const previewScreens = [
  {
    src: '/images/home/romind-kids-preview-times.png',
    title: 'Times',
    desc: 'A child-friendly time sharing system designed around kindness, limits, and parent-aware structure.',
  },
  {
    src: '/images/home/romind-kids-preview-gift-card.png',
    title: 'Times Gift Cards',
    desc: 'Collectible time cards for safe gifting between friends, built without random paid mechanics.',
  },
  {
    src: '/images/home/romind-kids-preview-rewards.png',
    title: 'Rewards for Generosity',
    desc: 'Positive recognition for thoughtful behavior, not popularity pressure or public ranking.',
  },
  {
    src: '/images/home/romind-kids-preview-messenger.png',
    title: 'Messenger',
    desc: 'Safe messages, platform updates, and direct access to RONI guidance inside the social layer.',
  },
  {
    src: '/images/home/romind-kids-preview-chat.png',
    title: 'Safe Chat',
    desc: 'Kind communication with visible support prompts, friendly reactions, and calm guidance.',
  },
  {
    src: '/images/home/romind-kids-preview-clubs.png',
    title: 'Clubs',
    desc: 'Creative communities for art, science, music, writing, and shared interests.',
  },
  {
    src: '/images/home/romind-kids-preview-safety-space.png',
    title: 'Safety Space',
    desc: 'A clear place for help, reporting, blocking, and trusted-adult support when something feels wrong.',
  },
  {
    src: '/images/home/romind-kids-preview-roni.png',
    title: 'RONI',
    desc: 'The in-app safety companion that helps children take the next calm step.',
  },
  {
    src: '/images/home/romind-kids-preview-create.png',
    title: 'Create Studio',
    desc: 'Tools for posts, photos, videos, music, stickers, and safe self-expression.',
  },
  {
    src: '/images/home/romind-kids-preview-email.png',
    title: 'Parent Email Check',
    desc: 'Account access patterns designed around privacy, protection, and parent-connected safety.',
  },
];

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ScreenCard = ({ screen, index, featured = false }) => (
  <article
    className={`group relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035] ${featured ? 'p-3 sm:p-4' : 'p-2.5'}`}
    style={{
      boxShadow: featured
        ? '0 0 60px rgba(157, 76, 221, 0.18), inset 0 1px 0 rgba(255,255,255,0.08)'
        : '0 0 28px rgba(0, 240, 255, 0.08), inset 0 1px 0 rgba(255,255,255,0.06)',
    }}
  >
    <div className="pointer-events-none absolute inset-0 rounded-[2rem] opacity-70" style={{ background: 'linear-gradient(135deg, rgba(255,0,127,0.16), rgba(0,240,255,0.12), rgba(157,76,221,0.16))' }} />
    <div className="relative rounded-[1.65rem] overflow-hidden bg-[#05040f] border border-white/10">
      <img
        src={screen.src}
        alt={`ROMIND KIDS ${screen.title} preview screen`}
        className="w-full aspect-[9/16] object-cover object-center transition-transform duration-700 group-hover:scale-[1.025]"
        loading={index < 3 ? 'eager' : 'lazy'}
      />
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-[#04030b] via-[#04030b]/78 to-transparent">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-2 h-2 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(0,240,255,0.9)]" />
          <span className="text-[10px] font-outfit font-semibold uppercase tracking-[0.18em] text-white/55">Preview screen</span>
        </div>
        <h3 className={`${featured ? 'text-2xl sm:text-3xl' : 'text-lg'} font-outfit font-bold text-white leading-tight`}>{screen.title}</h3>
        {featured && <p className="mt-2 text-sm sm:text-base text-white/62 leading-relaxed max-w-md">{screen.desc}</p>}
      </div>
    </div>
  </article>
);

const DesignShowcaseSection = () => (
  <section data-testid="design-showcase-section" className="relative py-24 bg-[#080714] overflow-hidden">
    <div className="absolute top-0 left-1/4 w-80 h-80 bg-violet-600/10 rounded-full blur-[120px] pointer-events-none" />
    <div className="absolute bottom-10 right-1/4 w-72 h-72 bg-cyan-600/10 rounded-full blur-[110px] pointer-events-none" />
    <div className="absolute inset-0 pointer-events-none opacity-35" style={{ background: 'radial-gradient(circle at 20% 20%, rgba(255,0,127,0.16), transparent 28%), radial-gradient(circle at 80% 15%, rgba(0,240,255,0.12), transparent 26%), radial-gradient(circle at 50% 85%, rgba(157,76,221,0.16), transparent 30%)' }} />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">Inside ROMIND KIDS</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-r from-pink-400 via-violet-400 to-cyan-300 bg-clip-text text-transparent tracking-tight mb-4">
              Younger age-group visual design.
            </h2>
            <p className="text-white/56 text-base sm:text-lg leading-relaxed">
              A first look at the ROMIND KIDS younger age-group visual direction: cosmic, safe, kind, creative, premium, child-friendly, and designed around meaningful interaction instead of endless scrolling. This is not the final production build, but the core ROMIND KIDS style is already set.
            </p>
          </div>

          <div className="w-fit rounded-full border border-fuchsia-400/30 bg-fuchsia-500/10 px-4 py-2 text-xs sm:text-sm font-outfit font-semibold uppercase tracking-[0.14em] text-fuchsia-100 shadow-[0_0_24px_rgba(255,0,127,0.12)]">
            Early preview · UI may evolve before launch
          </div>
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-start">
        <FadeIn delay={0} className="lg:col-span-5">
          <ScreenCard screen={previewScreens[0]} index={0} featured />
        </FadeIn>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {previewScreens.slice(1).map((screen, index) => (
            <FadeIn key={screen.title} delay={(index + 1) * 55}>
              <ScreenCard screen={screen} index={index + 1} />
            </FadeIn>
          ))}
        </div>
      </div>

      <FadeIn delay={520}>
        <div
          className="mt-10 rounded-[2rem] border border-[#2f2a45]/80 bg-[#090814]/88 p-5 sm:p-6 lg:p-7 transition-all duration-300 hover:border-[#5d5578]/80 hover:bg-[#0c0b18]/92 hover:shadow-[0_0_28px_rgba(116,105,160,0.14)]"
          style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.045), 0 18px 55px rgba(0,0,0,0.22)' }}
        >
          <div className="mb-5 flex items-center gap-2 text-[11px] font-outfit font-semibold uppercase tracking-[0.18em] text-white/42">
            <span className="h-1.5 w-1.5 rounded-full bg-white/35" />
            Early design note
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm sm:text-base text-white/58 leading-relaxed">
            <div className="rounded-2xl border border-[#2a2638] bg-[#11101d]/68 p-4 transition-all duration-300 hover:border-[#6b6384]/70 hover:bg-[#151322]/78 hover:shadow-[0_0_18px_rgba(116,105,160,0.10)]">
              <div className="font-outfit font-semibold text-white/86 mb-2">Why we show it now</div>
              To show the younger age-group visual atmosphere early while keeping the final production build private.
            </div>
            <div className="rounded-2xl border border-[#2a2638] bg-[#11101d]/68 p-4 transition-all duration-300 hover:border-[#6b6384]/70 hover:bg-[#151322]/78 hover:shadow-[0_0_18px_rgba(116,105,160,0.10)]">
              <div className="font-outfit font-semibold text-white/86 mb-2">What may change</div>
              Layout details, copy, icons, motion, and final feature behavior may still be refined before launch.
            </div>
            <div className="rounded-2xl border border-[#2a2638] bg-[#11101d]/68 p-4 transition-all duration-300 hover:border-[#6b6384]/70 hover:bg-[#151322]/78 hover:shadow-[0_0_18px_rgba(116,105,160,0.10)]">
              <div className="font-outfit font-semibold text-white/86 mb-2">What will stay</div>
              A calm cosmic style, kindness, creativity, premium feel, and meaningful interaction remain the core direction.
            </div>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

export default DesignShowcaseSection;
